/* CART DRAWER V2 controller — the ONE owner of drawer behavior.
 *
 * Design rules:
 * - All money/markup renders server-side (snippets/cart-drawer-v2.liquid).
 *   After any cart mutation this controller re-fetches /cart?view=ajax-drawer
 *   and swaps its own contents. No client-side price math, ever.
 * - Public API for other sections (replaces the old innerHTML hand-patching):
 *     document.dispatchEvent(new CustomEvent('cart:refresh'))   // re-render
 *     theme.drawer.open('right', 'cart-drawer')                 // open (unchanged)
 *   The drawer also self-refreshes whenever it is opened, so legacy sections
 *   that only open it still show fresh contents.
 * - Routine module: suggestion adds tag lines with _source: cart-builder and
 *   a shared _bundle_id (cart-builder-*). First engagement adopts the cart's
 *   other qualifying lines into the group; after that the group must track
 *   the cart — refresh() runs an absorption pass that folds qualifying loose
 *   lines (PDP / quick-add adds land untagged) into the group, so the tier
 *   keeps paying and the drawer never strands a mixed cart. The discount
 *   itself is applied by the bundle-discount Shopify Function — never
 *   simulated here.
 */
(function () {
  'use strict';
  if (customElements.get('cart-drawer-root')) return;

  var REFRESH_URL = '/cart?view=ajax-drawer';
  var GROUP_PREFIX = 'cart-builder-';

  function ga4(event, params) {
    // The site loads gtag.js directly (GTM is commented out in theme.liquid),
    // so events MUST go through gtag() — plain dataLayer.push objects are
    // only read by GTM and would evaporate. Fallback push kept for a future
    // GTM re-enable.
    try {
      var p = Object.assign({ module: 'cart-builder' }, params || {});
      if (typeof window.gtag === 'function') {
        window.gtag('event', event, p);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: event }, p));
      }
    } catch (e) { /* analytics must never break the cart */ }
  }

  var CartDrawer = /** @type {any} */ (function () {
    function C() { return Reflect.construct(HTMLElement, [], C); }
    C.prototype = Object.create(HTMLElement.prototype);
    C.prototype.constructor = C;

    C.prototype.connectedCallback = function () {
      if (this._wired) return;
      this._wired = true;
      this._busy = false;
      this._prevRate = this.cfg().tierRate || 0;
      this.bindGlobal();
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      ga4('module_viewed', { cart_units: this.cfg().cartUnits, tier_rate: this.cfg().tierRate });
    };

    C.prototype.cfg = function () {
      try {
        var el = this.querySelector('[data-cdv2-config]');
        return el ? JSON.parse(el.textContent) : {};
      } catch (e) { return {}; }
    };

    // ---------- global wiring (once per page) ----------
    C.prototype.bindGlobal = function () {
      if (window.__cdv2Global) return;
      window.__cdv2Global = true;
      var refresh = function () {
        var root = document.querySelector('cart-drawer-root');
        if (root) root.refresh();
      };
      document.addEventListener('cart:refresh', refresh);
      window.addEventListener('theme:cart:updated', refresh);
      // Self-refresh on open so legacy sections that only open the drawer
      // (their innerHTML patches now no-op) still show fresh contents.
      // Watch the drawer instance's aria-expanded — drawer.js toggles it on
      // EVERY open, including programmatic theme.drawer.open() calls from
      // quick-add / homepage rows that never click a [data-drawer-open].
      var inst = document.querySelector('.drawer--instance[data-side="right"]');
      if (inst && window.MutationObserver) {
        new MutationObserver(function () {
          if (inst.getAttribute('aria-expanded') === 'true') refresh();
        }).observe(inst, { attributes: true, attributeFilter: ['aria-expanded'] });
      }
      // Belt-and-suspenders: click trigger + the theme's add events.
      document.addEventListener('click', function (e) {
        var t = e.target && e.target.closest && e.target.closest('[data-drawer-open]');
        if (t) setTimeout(refresh, 150);
      });
      window.addEventListener('theme:cart:productAdded', refresh);
      window.addEventListener('cart:updated', refresh);
      document.addEventListener('cart:updated', refresh);
    };

    // ---------- refresh: fetch server HTML, swap self ----------
    C.prototype.refresh = function () {
      var self = this;
      return fetch(REFRESH_URL)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('cart-drawer-root');
          if (!fresh) return;
          var newRate = 0;
          try { newRate = JSON.parse(fresh.querySelector('[data-cdv2-config]').textContent).tierRate || 0; } catch (e) {}
          self.innerHTML = fresh.innerHTML;
          for (var i = 0; i < fresh.attributes.length; i++) {
            self.setAttribute(fresh.attributes[i].name, fresh.attributes[i].value);
          }
          if (newRate > self._prevRate) self.pulse();
          if (newRate !== self._prevRate && newRate > 0) ga4('tier_unlocked', { tier_rate: newRate });
          self._prevRate = newRate;
          self.updateHeaderCount();
          // Absorption pass (plan §4: the group tracks cart changes): a line
          // added outside the module (PDP, quick-add) lands untagged. If the
          // cart has an engaged group, fold loose lines in and re-render so
          // the tier pays. Guard prevents re-entry; this frame is transient,
          // so skip its view analytics.
          var cfg = self.cfg();
          if (cfg.engaged && cfg.looseUnits > 0 && !self._absorbing) {
            self._absorbing = true;
            return self.maintainGroup(cfg.builderGroupId)
              .then(function () { return self.refresh(); })
              .finally(function () { self._absorbing = false; });
          }
          var sugs = self.querySelectorAll('.cdv2__sug');
          if (sugs.length) {
            ga4('suggestion_shown', {
              count: sugs.length,
              slots: Array.prototype.map.call(sugs, function (r) { return r.dataset.slot; }).join(','),
              cart_units: newRate >= 0 ? (self.cfg().cartUnits || 0) : 0
            });
          }
          if (self.querySelector('.cdv2__complete')) ga4('routine_completed', {});
        })
        .catch(function (err) { console.error('[cart-drawer-v2] refresh failed', err); });
    };

    C.prototype.pulse = function () {
      this.querySelectorAll('.cdv2__kicker-note').forEach(function (el) {
        el.classList.add('pulse');
        el.addEventListener('animationend', function () { el.classList.remove('pulse'); }, { once: true });
      });
    };

    C.prototype.updateHeaderCount = function () {
      // Delegate to the theme's own header updater (sets data-item-count on
      // .cart--external--icon and textContent ONLY on dedicated count badges).
      // Never write text into icon wrappers — that erases the bag SVG.
      var t = window.theme;
      if (t && t.cart && typeof t.cart.fetchTotals === 'function') {
        try { t.cart.fetchTotals(); return; } catch (e) { /* fall through */ }
      }
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        document.querySelectorAll('.cart--external--icon').forEach(function (el) {
          el.setAttribute('data-item-count', cart.item_count);
        });
      }).catch(function () {});
    };

    C.prototype.setBusy = function (on) {
      this._busy = on;
      var scroll = this.querySelector('.cdv2__scroll');
      if (scroll) scroll.classList.toggle('is-busy', on);
    };

    // ---------- event delegation ----------
    C.prototype.onClick = function (e) {
      var t = e.target;
      var self = this;
      if (this._busy) return;

      var qty = t.closest && t.closest('[data-qty-change]');
      if (qty) {
        var lineEl = qty.closest('[data-line-key]');
        var valEl = lineEl.querySelector('.cdv2__qty-val');
        var next = parseInt(valEl.textContent, 10) + parseInt(qty.dataset.qtyChange, 10);
        return this.changeLine(lineEl.dataset.lineKey, Math.max(0, next));
      }
      var rm = t.closest && t.closest('[data-remove-line]');
      if (rm) return this.changeLine(rm.dataset.removeLine, 0);

      var rmb = t.closest && t.closest('[data-remove-bundle]');
      if (rmb) return this.removeBundle(rmb.dataset.removeBundle);

      var add = t.closest && t.closest('[data-sug-add]');
      if (add) return this.suggestionAdd(add);

      var reco = t.closest && t.closest('[data-reco-add]');
      if (reco) return this.recoAdd(reco);

      var swap = t.closest && t.closest('[data-swap]');
      if (swap) return this.swapSuggestion(swap);

      var adopt = t.closest && t.closest('[data-adopt]');
      if (adopt) return this.adoptCart(true);

      var applyBtn = t.closest && t.closest('[data-apply-discount]');
      if (applyBtn) return this.applyDiscount();

      if (t.closest && t.closest('[data-builder-link]')) {
        ga4('builder_link_clicked', { cart_units: this.cfg().cartUnits });
        // natural navigation proceeds
      }
    };

    C.prototype.onChange = function (e) {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-cart-note')) {
        var note = e.target.value;
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note })
        }).catch(function () {});
      }
    };

    // ---------- cart mutations ----------
    C.prototype.changeLine = function (key, quantity) {
      var self = this;
      this.setBusy(true);
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function () { return self.refresh(); })
        .finally(function () { self.setBusy(false); });
    };

    C.prototype.removeBundle = function (bundleId) {
      var self = this;
      this.setBusy(true);
      return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        var updates = {};
        cart.items.forEach(function (item) {
          if ((item.properties || {})._bundle_id === bundleId) updates[item.key] = 0;
        });
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      })
        .then(function () { return self.refresh(); })
        .finally(function () { self.setBusy(false); });
    };

    // ---------- routine module ----------
    C.prototype.groupId = function () {
      var existing = this.cfg().builderGroupId;
      return existing || GROUP_PREFIX + Date.now();
    };

    C.prototype.suggestionAdd = function (btn) {
      var self = this;
      var variantId = parseInt(btn.dataset.sugAdd, 10);
      if (!variantId) return;
      var firstEngagement = !this.cfg().engaged;
      var gid = this.groupId();
      btn.disabled = true;
      this.setBusy(true);
      ga4('suggestion_added', { product_handle: btn.dataset.handle, slot: (btn.closest('[data-slot]') || {}).dataset ? btn.closest('[data-slot]').dataset.slot : '' });
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _source: 'cart-builder', _bundle_id: gid }
        })
      })
        .then(function () { return firstEngagement ? self.adoptCart(false, gid) : self.maintainGroup(gid); })
        .then(function () { return self.refresh(); })
        .finally(function () { self.setBusy(false); });
    };

    // Kit-in-cart delight reco: a plain add, NO cart-builder tagging. The
    // routine is already complete; this is a standalone extra (e.g. body SPF),
    // so it must not join the builder group or trigger routine-tier pricing.
    C.prototype.recoAdd = function (btn) {
      var self = this;
      var variantId = parseInt(btn.dataset.recoAdd, 10);
      if (!variantId) return;
      btn.disabled = true;
      this.setBusy(true);
      ga4('kit_reco_added', { product_handle: btn.dataset.handle });
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function () { return self.refresh(); })
        .finally(function () { self.setBusy(false); });
    };

    // Tag every qualifying, untagged line into gid. Rewrites run sequentially
    // — parallel /cart/change.js calls race on cart state.
    C.prototype.tagLooseLines = function (gid) {
      return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        var seq = Promise.resolve();
        cart.items.forEach(function (item) {
          var props = item.properties || {};
          if (props._bundle_id) return;             // already grouped (builder or legacy)
          if (item.gift_card || item.final_price <= 0) return;
          var merged = Object.assign({}, props, { _bundle_id: gid, _source: props._source || 'cart-adopted' });
          seq = seq.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: merged })
            });
          });
        });
        return seq;
      });
    };

    // Adopt: tag every qualifying, untagged line into the builder group.
    // interactive=true is the "Looks like a routine" tap (its own GA4 event).
    C.prototype.adoptCart = function (interactive, gid) {
      var self = this;
      gid = gid || this.groupId();
      if (interactive) { this.setBusy(true); ga4('cart_adopted', { cart_units: this.cfg().cartUnits }); }
      var chain = this.tagLooseLines(gid);
      if (!interactive) return chain;
      return chain.then(function () { return self.refresh(); }).finally(function () { self.setBusy(false); });
    };

    // Once engaged, the group tracks cart changes (plan §4): fold qualifying
    // loose lines into the existing group. No-op when no group exists — the
    // next engagement mints a fresh id.
    C.prototype.maintainGroup = function (gid) {
      gid = gid || this.cfg().builderGroupId;
      if (!gid) return Promise.resolve();
      return this.tagLooseLines(gid);
    };

    C.prototype.swapSuggestion = function (btn) {
      var row = btn.closest('.cdv2__sug');
      if (!row) return;
      var addBtn = row.querySelector('[data-sug-add],[data-reco-add]');
      if (!addBtn) return;
      var addKey = ('sugAdd' in addBtn.dataset) ? 'sugAdd' : 'recoAdd';
      var name = row.querySelector('.cdv2__sug-name');
      var price = row.querySelector('.cdv2__sug-price');
      var img = row.querySelector('.cdv2__sug-img img');
      // capture current (to become the alternate)
      var cur = {
        handle: addBtn.dataset.handle,
        title: name.textContent,
        price: price.textContent,
        img: img ? img.src : '',
        variant: addBtn.dataset[addKey]
      };
      name.textContent = btn.dataset.altTitle;
      price.textContent = btn.dataset.altPrice;
      if (img && btn.dataset.altImg) img.src = btn.dataset.altImg;
      addBtn.dataset[addKey] = btn.dataset.altVariant;
      addBtn.dataset.handle = btn.dataset.altHandle;
      // why-line: swap descriptions along with the product (nudge clause, if
      // any, is dropped until the next server render re-derives it)
      var why = row.querySelector('.cdv2__sug-why');
      if (why) {
        var curWhy = row.dataset.why || '';
        why.textContent = btn.dataset.altWhy || '';
        row.dataset.why = btn.dataset.altWhy || '';
        btn.dataset.altWhy = curWhy;
      }
      btn.dataset.altHandle = cur.handle;
      btn.dataset.altTitle = cur.title;
      btn.dataset.altPrice = cur.price;
      btn.dataset.altImg = cur.img;
      btn.dataset.altVariant = cur.variant;
      btn.textContent = 'or ' + cur.title.replace(' Serum', '').replace(' Moisturizer', '') + ' →';
      ga4('suggestion_swapped', { product_handle: addBtn.dataset.handle });
    };

    // ---------- discount code ----------
    C.prototype.applyDiscount = function () {
      var self = this;
      var input = this.querySelector('.cdv2__discount-input');
      var msg = this.querySelector('.cdv2__discount-msg');
      var code = (input.value || '').trim();
      if (!code) return;
      this.setBusy(true);
      fetch('/discount/' + encodeURIComponent(code))
        .then(function () { return self.refresh(); })
        .then(function () {
          var applied = self.querySelector('.cdv2__discount-applied');
          var m = self.querySelector('.cdv2__discount-msg');
          if (m) {
            if (applied && applied.textContent.toUpperCase().indexOf(code.toUpperCase()) !== -1) {
              m.textContent = 'Applied.';
              m.classList.add('is-ok');
              m.classList.remove('is-err');
            } else {
              m.textContent = 'That code didn\u2019t apply to this cart.';
              m.classList.add('is-err');
              m.classList.remove('is-ok');
            }
          }
        })
        .finally(function () { self.setBusy(false); });
      if (msg) msg.textContent = '';
    };

    return C;
  })();

  customElements.define('cart-drawer-root', CartDrawer);
})();
